/**
 * Enum representing commonly used MIME types.
 *
 * @description
 * This enum provides a convenient way to reference commonly used MIME types in web development.
 * It is not exhaustive and may be extended in the future to include additional MIME types as needed.
 */
export enum MIMETypeEnum {
    /**
     * Represents the MIME type for multipart form data.
     */
    APPLICATION_FORM_DATA = 'multipart/form-data',

    /**
     * Represents the MIME type for JSON data.
     */
    APPLICATION_JSON = 'application/json',

    /**
     * Represents the MIME type for Microsoft Word documents (.doc).
     */
    APPLICATION_MSWORD = 'application/msword',

    /**
     * Represents the MIME type for Office Open XML Word documents (.docx).
     */
    APPLICATION_VND_OPENXML_WORDPROCESSINGML_DOCUMENT = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',

    /**
     * Represents the MIME type for arbitrary binary data.
     */
    APPLICATION_OCTET_STREAM = 'application/octet-stream',

    /**
     * Represents the MIME type for PDF documents.
     */
    APPLICATION_PDF = 'application/pdf',

    /**
     * Represents the MIME type for Microsoft Excel spreadsheets. Also, may be used when a .CSV is uploaded via
     * Firefox on Windows where MS Office is installed...
     *
     * @see https://planetjon.ca/uploading-csv-unexpected-mime-5796
     */
    APPLICATION_VND_MS_EXCEL = 'application/vnd.ms-excel',

    /**
     * Represents the MIME type for Microsoft PowerPoint presentations.
     */
    APPLICATION_VND_MS_POWERPOINT = 'application/vnd.ms-powerpoint',

    /**
     * Represents the MIME type for form URL-encoded data.
     */
    APPLICATION_X_WWW_FORM_URLENCODED = 'application/x-www-form-urlencoded',

    /**
     * Represents the MIME type for Extensible Markup Language (XML) documents.
     */
    APPLICATION_XML = 'application/xml',

    /**
     * Represents the MIME type for ZIP archives.
     */
    APPLICATION_ZIP = 'application/zip',

    /**
     * Represents the MIME type for MPEG-1 Audio Layer III (MP3) audio.
     */
    AUDIO_MPEG = 'audio/mpeg',

    /**
     * Represents the MIME type for Waveform Audio Format (WAV) audio.
     */
    AUDIO_WAV = 'audio/wav',

    /**
     * Represents the MIME type for Graphics Interchange Format (GIF) images.
     */
    IMAGE_GIF = 'image/gif',

    /**
     * Represents the MIME type for JPEG images.
     */
    IMAGE_JPEG = 'image/jpeg',

    /**
     * Represents the MIME type for Portable Network Graphics (PNG) images.
     */
    IMAGE_PNG = 'image/png',

    /**
     * Represents the MIME type for Scalable Vector Graphics (SVG) images.
     */
    IMAGE_SVG_XML = 'image/svg+xml',

    /**
     * Represents the MIME type for Cascading Style Sheets (CSS).
     */
    TEXT_CSS = 'text/css',

    /**
     * Represents the MIME type for Column separated values (CSV).
     */
    TEXT_CSV = 'text/csv',

    /**
     * Represents the MIME type for Hypertext Markup Language (HTML) documents.
     */
    TEXT_HTML = 'text/html',

    /**
     * Represents the MIME type for JavaScript code.
     */
    TEXT_JAVASCRIPT = 'text/javascript',

    /**
     * Represents the MIME type for plain text.
     */
    TEXT_PLAIN = 'text/plain',

    /**
     * Represents the MIME type for MPEG video.
     */
    VIDEO_MPEG = 'video/mpeg',

    /**
     * Represents the MIME type for MPEG-4 video.
     */
    VIDEO_MP4 = 'video/mp4',
}
